/**
 * Generic System Columns Factory
 * 
 * Creates non-configurable system columns (Assigned-to, Created, Comments, Likes)
 * that appear on the right side of list tables. These columns are separate from
 * meta-driven user-configurable columns.
 */

import type { ColumnDef } from "@tanstack/react-table";
import { MessageCircle } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { LikeCell } from "./LikeCell";
import { UserAvatar } from "./UserAvatar";
import type { UserAvatarMap } from "../api/useUserAvatars";
import { parseLikedBy } from "../utils/parseLikedBy";

type SystemColumnsConfig = {
  doctype: string;
  assignedToField: string;
  commentCountField: string;
  likedByField: string;
  createdField: string;
  showAssignedTo: boolean;
  showComments: boolean;
  showLikes: boolean;
  showCreated: boolean;
  avatarMap?: UserAvatarMap; // Optional: map of userId -> avatar info for real avatars
};

/**
 * Create system columns for a DocType list table
 * 
 * These columns are always appended to the right side and are not
 * configurable via List Settings.
 */
export function createSystemColumns<T>(
  config: SystemColumnsConfig
): ColumnDef<T>[] {
  const cols: ColumnDef<T>[] = [];

  const {
    doctype,
    assignedToField,
    commentCountField,
    likedByField,
    createdField,
    showAssignedTo,
    showComments,
    showLikes,
    showCreated,
    avatarMap = {}, // Default to empty map if not provided
  } = config;

  // Assigned To avatars (first system column - add left border for visual grouping)
  if (showAssignedTo) {
    cols.push({
      id: "__assigned_to",
      header: () => (
        <div className="flex items-center justify-center pl-3 border-l border-border/80 pr-0">
          {/* No icon - empty header */}
        </div>
      ),
      cell: ({ row }) => {
        const raw = row.original as Record<string, unknown>;
        const value = raw[assignedToField] as string | string[] | null | undefined;
        
        const users = parseLikedBy(value);
        if (!users.length) return null;

        // Show up to 3 avatars with real images if available
        return (
          <div className="flex -space-x-2 justify-center pl-3 border-l border-border/80 pr-0">
            {users.slice(0, 3).map((userId, idx) => {
              const avatarInfo = avatarMap[userId];
              const displayName = avatarInfo?.fullName || userId;
              const imageUrl = avatarInfo?.imageUrl;
              
              return (
                <UserAvatar
                  key={`${userId}-${idx}`}
                  imageUrl={imageUrl}
                  fallbackText={userId}
                  title={displayName}
                  size="lg"
                />
              );
            })}
            {users.length > 3 && (
              <div
                className="h-8 w-8 rounded-full border-2 border-background bg-muted flex items-center justify-center text-xs font-medium"
                title={users.slice(3).map((u) => avatarMap[u]?.fullName || u).join(", ")}
              >
                +{users.length - 3}
              </div>
            )}
          </div>
        );
      },
      enableSorting: false,
      enableColumnFilter: false,
      size: 45, // Tighter width
      minSize: 45,
      maxSize: 45,
    });
  }

  // Created date
  if (showCreated) {
    cols.push({
      id: "__created",
      header: () => (
        <div className="flex items-center justify-center">
          {/* No icon - empty header */}
        </div>
      ),
      cell: ({ row }) => {
        const raw = row.original as Record<string, unknown>;
        const value = raw[createdField] as string | undefined;
        
        if (!value) {
          return (
            <div className="flex items-center justify-center">
              <span className="text-muted-foreground text-xs">-</span>
            </div>
          );
        }

        const date = new Date(value);
        if (isNaN(date.getTime())) {
          return (
            <div className="flex items-center justify-center">
              <span className="text-muted-foreground text-xs">{value}</span>
            </div>
          );
        }

        const relativeTime = formatDistanceToNow(date, { addSuffix: true });
        const fullDate = date.toLocaleString();

        return (
          <div className="flex items-center justify-center">
            <span
              title={fullDate}
              className="text-xs text-muted-foreground whitespace-nowrap"
            >
              {relativeTime}
            </span>
          </div>
        );
      },
      enableSorting: false,
      enableColumnFilter: false,
      // Wide enough for the longest relative time ("about 1 hour ago") PLUS
      // side breathing room so the centered text isn't jammed edge-to-edge.
      size: 132,
      minSize: 132,
      maxSize: 132,
    });
  }

  // Comments
  if (showComments) {
    cols.push({
      id: "__comments",
      accessorKey: commentCountField, // ✅ Use commentCountField prop (e.g., "comment_count")
      header: () => (
        <div className="flex items-center justify-center">
          {/* No icon - empty header */}
        </div>
      ),
      cell: ({ getValue }) => {
        // Use getValue() which automatically uses this column's accessorKey
        const raw = getValue() as number | null | undefined;
        const numericCount = typeof raw === 'number' ? raw : 0;
        
        if (!numericCount || numericCount === 0) {
          // Grey icon when 0 or undefined
          return (
            <div className="flex items-center justify-center">
              <span className="inline-flex items-center justify-center h-6 w-6 text-muted-foreground/60">
                <MessageCircle className="h-4 w-4" />
              </span>
            </div>
          );
        }

        // Icon + number when count > 0 - outline icon with primary blue color
        return (
          <div className="flex items-center justify-center">
            <div className="inline-flex items-center gap-1 text-xs">
              <MessageCircle className="h-4 w-4 text-primary" />
              <span>{numericCount}</span>
            </div>
          </div>
        );
      },
      enableSorting: false,
      enableColumnFilter: false,
      size: 42, // Standard width
      minSize: 42,
      maxSize: 42,
    });
  }

  // Likes / Favorites
  if (showLikes) {
    cols.push({
      id: "__like",
      header: () => (
        <div className="flex items-center justify-center">
          {/* No icon - empty header */}
        </div>
      ),
      cell: ({ row }) => {
        const raw = row.original as Record<string, unknown>;
        const likedByRaw = raw[likedByField] as string | string[] | undefined;
        const docname = raw.name as string;

        return (
          <div className="flex items-center justify-center">
            <LikeCell
              doctype={doctype}
              name={docname}
              likedByRaw={likedByRaw}
            />
          </div>
        );
      },
      enableSorting: false,
      enableColumnFilter: false,
      size: 42, // Tighter width
      minSize: 42,
      maxSize: 42,
    });
  }

  return cols;
}

